import { Injectable } from "@angular/core"
import { AngularFirestoreCollection } from "@angular/fire/firestore"
import { CustomFields, FileUrl, TestCase, UserDefaultColumns } from "@tr/models"
import { getDataItem, TestCaseUtils } from "@tr/utils"
import * as firebase from "firebase/app"
import { DataService } from "./data.service"
import { FirebaseService } from "./firebase.service"
import { UserDefaultsBaseService } from "./user-defaults-base.service"
import { SwalService } from "./swal.service"

export interface DeleteFileResult {
  success: boolean
  path?: string
}

export interface DeleteFileResult {
  success: boolean
  path?: string
}

@Injectable({
  providedIn: "root",
})
export class TestCasesService extends UserDefaultsBaseService<UserDefaultColumns, TestCase> {
  public userDefaultsDocName = "TestCases"


  public getId = (item: TestCase) => item._id

  constructor(fb: FirebaseService, dataService: DataService) {
    super(fb, dataService)
  }

  public getCollection(company: string, project: string): AngularFirestoreCollection {
    // TODO: Does test cases have different internal collection
    return this.firebaseService
      .collection("companies")
      .doc(company)
      .collection("projects")
      .doc(project)
      .collection<TestCase>("Test Cases")
  }

  public async addTestCase(testCase: TestCase = TestCaseUtils.emptyTestCase()): Promise<TestCase> {
    const testIDNumber = (this.dataService.totalTestCases.value || 0) + 1
    const testID = `${testIDNumber}`

    const newTestCaseRef = await this
      .getCollection(this.dataService.company.value, this.dataService.project.value)
      .add({
        ...testCase,
        testID,
      })
    await this.firebaseService
      .collection("companies")
      .doc(this.dataService.company.value)
      .collection("projects")
      .doc(this.dataService.project.value)
      .update({
        unusedTestFiles: [],
        totalTestCases: testIDNumber,
      })

    testCase.testID = testID
    testCase._id = newTestCaseRef.id
    this.dataMap[testCase._id] = testCase
    return testCase
  }

  public async getData(company: string, project: string): Promise<TestCase[]> {
    const collectionSnapshot = await this.getCollection(company, project).ref.get()
    this.data = this.getDataItems(collectionSnapshot)
    return this.data
  }

  public getDataItems(snapshot: firebase.firestore.QuerySnapshot): TestCase[] {
    return snapshot.empty ? [] : this.processDataItems(snapshot.docs)
  }

  public processDataItems(testCaseSnapshots: firebase.firestore.DocumentSnapshot[]): TestCase[] {
    return testCaseSnapshots.map<TestCase>(testCaseSnapshot => {
      if (!testCaseSnapshot.exists) { return null }
      const testCase = testCaseSnapshot.data() as TestCase
      testCase._id = testCaseSnapshot.id
      this.dataMap[testCaseSnapshot.id] = testCase
      return testCase
    }).filter(testCase => !!testCase)
  }

  public async getDefaultCustomFields(): Promise<void> {
    const company = this.dataService.company.value
    const project = this.dataService.project.value
    const testCaseSettingsPath = `companies/${company}/projects/${project}/TestCasesSettings/customVariables`
    this.customFields = await getDataItem(this.firebaseService.doc(testCaseSettingsPath), false)
    this.customFieldKeys = Object.keys(this.customFields || {}).sort()
  }

  public updateWithDefaultCustomFields(obj: any): CustomFields {
    (this.customFieldKeys || []).forEach(key => {
      if (obj[key] === undefined) {
        obj[key] = this.customFields[key]
      }
    })

    return obj
  }

  public async getTestCasesByIds(company: string, project: string, ids: string[]): Promise<TestCase[]> {
    return this.processDataItems(
      await Promise.all(ids.map(async id => this.getCollection(company, project).doc(id).ref.get()))
    )
  }

  public async getById(company: string, project: string, id: string): Promise<TestCase> {
    return getDataItem<TestCase>(
      this.getCollection(company, project).doc(id),
    )
  }

  public async update(
    company: string,
    project: string,
    testCaseId: string,
    testToUpdate: any,
  ): Promise<void> {
    return this.getCollection(company, project)
      .doc(testCaseId)
      .update(testToUpdate)
  }

  public async delete(company: string, project: string, id: string): Promise<void> {
    await this.firebaseService
      .collection("companies")
      .doc(company)
      .collection("projects")
      .doc(project)
      .collection("Test Cases")
      .doc(id)
      .delete()
  }

  public getFilePath(fileName: string, company: string, project: string, testCaseId: string): string {
    return `${company}/${project}/testCases/${testCaseId}/files/${new Date().getTime()}_${fileName}`
  }

  public async deleteFiles(fileUrls: string[]): Promise<DeleteFileResult[]> {
    return Promise.all(
      fileUrls.map(async fileUrl => this.deleteFile(fileUrl))
    )
  }

  public async deleteFile(fileUrl: string, pathFromUrl = true): Promise<DeleteFileResult> {
    let path = fileUrl
    if (pathFromUrl) {
      path = decodeURIComponent(fileUrl.split("/o/")[1].split("?")[0])
    }
    try {
      const fileRef = firebase
          .storage()
          .ref()
          .child(path)

      await fileRef.delete()
      return { success: true }
    } catch (err) {
      console.error(err)
      return { success: false, path }
    }
  }

  public async uploadFiles(files: File[], company: string, project: string, testCaseId): Promise<FileUrl[]> {
    if (!files || files.length === 0) { return [] }
    return await Promise.all(
      files.map(async file => {
        const path = this.getFilePath(file.name, company, project, testCaseId)
        const fileStorageRef = firebase
          .storage()
          .ref()
          .child(path)
        const dateCreated = firebase.firestore.Timestamp.fromDate(new Date())
        await fileStorageRef
          .put(file)
          .catch(err => { console.error(err) })

        const url = await fileStorageRef.getDownloadURL()

        return {
          url,
          dateCreated,
          name: file.name,
          newName: path.split("/files/")[1],
          filePath: path,
        }
      })
    )
  }
}
